import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 2rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const Title = styled.h2`
  color: white;
  font-size: 1.5rem;
  margin: 0;
`;

const AddButton = styled.button`
  background: linear-gradient(135deg, #27ae60, #2ecc71);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(39, 174, 96, 0.4);
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: 2rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 1rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  color: white;
`;

const TableHeader = styled.th`
  background: rgba(255, 255, 255, 0.1);
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
  white-space: nowrap;
`;

const TableCell = styled.td`
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  white-space: nowrap;
`;

const ActionButton = styled.button`
  background: ${props => props.danger ? '#e74c3c' : '#3498db'};
  color: white;
  border: none;
  border-radius: 5px;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  cursor: pointer;
  margin-right: 0.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  color: #333;
  margin-bottom: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: #333;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const CancelButton = styled.button`
  background: #95a5a6;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  
  &:hover {
    background: #7f8c8d;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

Select.displayName = 'Select';

const SearchContainer = styled.div`
  position: relative;
`;

const SearchInput = styled(Input)`
  padding-right: 3rem;
`;

const SearchResults = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-top: none;
  border-radius: 0 0 8px 8px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
`;

const SearchResultItem = styled.div`
  padding: 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid #eee;
  color: #333;
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const LoadingText = styled.div`
  color: #666;
  padding: 0.75rem;
  font-style: italic;
`;

const NoResults = styled.div`
  color: #666;
  padding: 0.75rem;
  font-style: italic;
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: white;
  font-size: 1.2rem;
`;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function IPManagement({ showMessage }) {
  const [allowedIPs, setAllowedIPs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    userId: '',
    userEmail: '',
    userName: '',
    ipAddress: '',
    reason: '',
    notes: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  useEffect(() => {
    fetchIPs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Search students by email
  const searchStudents = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setSearchLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/students/search?email=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.data || []);
        setShowSearchResults(true);
      } else {
        setSearchResults([]);
        setShowSearchResults(true);
      }
    } catch (error) {
      console.error('Error searching students:', error);
      setSearchResults([]);
      setShowSearchResults(true);
    } finally {
      setSearchLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchStudents(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSearchResults && !event.target.closest('.search-container')) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSearchResults]);

  const selectStudent = (student) => {
    setFormData({
      ...formData,
      userId: student.id,
      userEmail: student.email,
      userName: student.full_name || student.email
    });
    setSearchQuery(student.email);
    setShowSearchResults(false);
  };

  const fetchIPs = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/ip-management`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAllowedIPs(data.data);
      } else {
        const error = await response.json();
        const errorMessage = typeof error.error === 'string' ? error.error : error.error?.message || 'Failed to fetch IP data';
        showMessage(errorMessage, 'error');
      }
    } catch (error) {
      console.error('Error fetching IPs:', error);
      const errorMessage = error.message || 'Network error';
      showMessage(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddIP = () => {
    setFormData({
      userId: '',
      userEmail: '',
      userName: '',
      ipAddress: '',
      reason: '',
      notes: ''
    });
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/ip-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId: formData.userId,
          ipAddress: formData.ipAddress,
          reason: formData.reason,
          notes: formData.notes
        })
      });

      if (response.ok) {
        showMessage('IP address added successfully', 'success');
        setShowModal(false);
        fetchIPs();
      } else {
        const error = await response.json();
        const errorMessage = typeof error.error === 'string' ? error.error : error.error?.message || 'Failed to add IP';
        showMessage(errorMessage, 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = error.message || 'Network error';
      showMessage(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveIP = async (ipId) => {
    if (!window.confirm('Are you sure you want to remove this IP address?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/ip-management/${ipId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        showMessage('IP removed successfully', 'success');
        fetchIPs();
      } else {
        const error = await response.json();
        const errorMessage = typeof error.error === 'string' ? error.error : error.error?.message || 'Failed to remove IP';
        showMessage(errorMessage, 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = error.message || 'Network error';
      showMessage(errorMessage, 'error');
    }
  };

  if (loading) {
    return <LoadingSpinner>Loading IP management data...</LoadingSpinner>;
  }

  return (
    <Container>
      <Header>
        <Title>IP Management</Title>
        <AddButton onClick={handleAddIP}>
          + Add IP Address
        </AddButton>
      </Header>

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <TableHeader>User</TableHeader>
              <TableHeader>Email</TableHeader>
              <TableHeader>IP Address</TableHeader>
              <TableHeader>Reason</TableHeader>
              <TableHeader>Added By (Admin)</TableHeader>
              <TableHeader>Added Date</TableHeader>
              <TableHeader>Actions</TableHeader>
            </tr>
          </thead>
          <tbody>
            {allowedIPs.map(ip => (
              <tr key={ip.id}>
                <TableCell>{ip.full_name || 'N/A'}</TableCell>
                <TableCell>{ip.email}</TableCell>
                <TableCell>{ip.ip}</TableCell>
                <TableCell>{ip.reason || 'N/A'}</TableCell>
                <TableCell>{ip.added_by_name || ip.added_by_email || 'N/A'}</TableCell>
                <TableCell>{new Date(ip.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <ActionButton danger onClick={() => handleRemoveIP(ip.id)}>
                    Remove
                  </ActionButton>
                </TableCell>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>

      {showModal && (
        <Modal onClick={() => setShowModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalTitle>Add IP Address</ModalTitle>
            
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Student Email</Label>
                <SearchContainer className="search-container">
                  <SearchInput
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Start typing student email..."
                    required
                  />
                  {showSearchResults && (
                    <SearchResults>
                      {searchLoading ? (
                        <LoadingText>Searching...</LoadingText>
                      ) : searchResults.length > 0 ? (
                        searchResults.map(student => (
                          <SearchResultItem
                            key={student.id}
                            onClick={() => selectStudent(student)}
                          >
                            <div><strong>{student.full_name || 'No name'}</strong></div>
                            <div style={{ fontSize: '0.9rem', color: '#666' }}>
                              {student.email}
                            </div>
                          </SearchResultItem>
                        ))
                      ) : (
                        <NoResults>No students found</NoResults>
                      )}
                    </SearchResults>
                  )}
                </SearchContainer>
                {formData.userName && (
                  <div style={{ 
                    marginTop: '0.5rem', 
                    fontSize: '0.9rem', 
                    color: '#27ae60',
                    fontWeight: '500'
                  }}>
                    Selected: {formData.userName} ({formData.userEmail})
                  </div>
                )}
              </FormGroup>
              
              <FormGroup>
                <Label>IP Address</Label>
                <Input
                  type="text"
                  value={formData.ipAddress}
                  onChange={e => setFormData({...formData, ipAddress: e.target.value})}
                  placeholder="192.168.1.1"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Reason</Label>
                <TextArea
                  value={formData.reason}
                  onChange={e => setFormData({...formData, reason: e.target.value})}
                  placeholder="Reason for adding this IP address"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Notes (Optional)</Label>
                <TextArea
                  value={formData.notes}
                  onChange={e => setFormData({...formData, notes: e.target.value})}
                  placeholder="Additional notes"
                />
              </FormGroup>

              <ButtonGroup>
                <CancelButton type="button" onClick={() => setShowModal(false)}>
                  Cancel
                </CancelButton>
                <SubmitButton type="submit">
                  Add IP
                </SubmitButton>
              </ButtonGroup>
            </form>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
}

export default IPManagement;
